import React from "react";

const plans = [
  {
    name: "Basic",
    key: "basic",
    price: "RM 399",
    period: "/month",
    description: "For small teams starting FinOps visibility.",
    features: [
      "1 cloud account",
      "Basic dashboard",
      "Monthly spend tracking",
      "Budget alerts",
      "Basic PDF report",
      "Email support",
    ],
    cta: "Start Free Trial",
    highlight: false,
    tag: "Starter",
  },
  {
    name: "Standard",
    key: "standard",
    price: "RM 1,299",
    period: "/month",
    description: "Best for growing businesses managing cloud spend.",
    features: [
      "Up to 3 cloud accounts",
      "Multi-cloud dashboard",
      "Anomaly detection",
      "Optimization insights",
      "AI executive summary",
      "Budget forecast",
      "Chargeback readiness",
      "Full PDF reports",
    ],
    cta: "Start Free Trial",
    highlight: true,
    tag: "Most Popular",
  },
  {
    name: "Enterprise",
    key: "enterprise",
    price: "Custom",
    period: "",
    description: "For enterprise FinOps governance and control.",
    features: [
      "Unlimited cloud accounts",
      "Multi-cloud control tower",
      "RBAC user roles",
      "SSO integration",
      "Audit logs",
      "Custom reports",
      "Dedicated support",
      "FinOps advisory",
    ],
    cta: "Contact Sales",
    highlight: false,
    tag: "Enterprise",
  },
];

function BrandTitle({ small = false }) {
  return (
    <div style={{ display: "flex", gap: small ? "6px" : "8px", alignItems: "center", whiteSpace: "nowrap" }}>
      <span style={{ color: "#FFFFFF" }}>TanSmartX</span>
      <span
        style={{
          background: "linear-gradient(90deg, #67E8F9, #22D3EE, #38BDF8)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textShadow: "0 0 22px rgba(56,189,248,0.42)",
        }}
      >
        FinOps
      </span>
    </div>
  );
}

function BrandLogo() {
  return (
    <img
      src="/logo-icon.png"
      alt="Tanvoracloud"
      style={{
        height: "52px",
        width: "auto",
        objectFit: "contain",
        display: "block",
        opacity: 1,
        filter: "brightness(1.25) contrast(1.15) drop-shadow(0 0 18px rgba(14,165,233,0.75))",
        transform: "scale(1.04)",
      }}
    />
  );
}

function PlanCard({ plan, isCurrent, planSaving, onClick }) {
  return (
    <div
      style={{
        ...styles.card,
        ...(plan.highlight ? styles.highlightCard : {}),
      }}
    >
      <div style={styles.cardGlow} />
      {plan.highlight && <div style={styles.popular}>{plan.tag}</div>}
      {isCurrent && <div style={styles.currentPlan}>Current Plan</div>}
      {!isCurrent && !plan.highlight && <div style={styles.planTag}>{plan.tag}</div>}

      <h2 style={styles.planName}>{plan.name}</h2>
      <p style={styles.description}>{plan.description}</p>

      <div style={styles.priceRow}>
        <span style={styles.price}>{plan.price}</span>
        <span style={styles.period}>{plan.period}</span>
      </div>

      <ul style={styles.featureList}>
        {plan.features.map((feature) => (
          <li key={feature} style={styles.featureItem}>
            <span style={styles.check}>✓</span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => onClick(plan)}
        disabled={isCurrent || planSaving}
        style={isCurrent ? styles.currentButton : plan.highlight ? styles.primaryButton : styles.secondaryButton}
      >
        {planSaving ? "Updating..." : isCurrent ? "Current Plan" : plan.cta}
      </button>
    </div>
  );
}

export default function Pricing({ onBackToDashboard, onSelectPlan, currentPlan = "basic", planSaving = false }) {
  const handlePlanClick = (plan) => {
    if (plan.key === "enterprise") {
      const activateDemo = window.confirm(
        "Activate Enterprise demo mode for this session? Press Cancel to contact sales instead."
      );

      if (!activateDemo) {
        window.location.href = "mailto:info@tanvoracloud.com?subject=TanSmartX%20FinOps%20Enterprise%20Plan";
        return;
      }
    }

    onSelectPlan?.(plan.key);
  };

  return (
    <div style={styles.page}>
      <div style={styles.backgroundGrid} />
      <div style={styles.orbOne} />
      <div style={styles.orbTwo} />

      <div style={styles.navbar}>
        <div style={styles.brandWrap}>
          <BrandLogo />
          <div>
            <div style={styles.brand}>
              <BrandTitle small />
            </div>
            <div style={styles.byline}>by Tanvoracloud</div>
          </div>
        </div>

        {onBackToDashboard && (
          <button onClick={onBackToDashboard} style={styles.navButton}>
            Back to Dashboard
          </button>
        )}
      </div>

      <main style={styles.main}>
        <div style={styles.heroPanel}>
          <div style={styles.badge}>SaaS Subscription Plans</div>
          <h1 style={styles.title}>Pricing Plans</h1>
          <p style={styles.subtitle}>
            Simple, transparent pricing for cloud financial visibility, governance, anomaly control, and optimization execution.
          </p>
          <div style={styles.trustRow}>
            <span style={styles.trustItem}>✓ Multi-cloud ready</span>
            <span style={styles.trustItem}>✓ Executive reporting</span>
            <span style={styles.trustItem}>✓ Enterprise governance</span>
          </div>
        </div>

        <div style={styles.grid}>
          {plans.map((plan) => (
            <PlanCard
              key={plan.name}
              plan={plan}
              isCurrent={currentPlan === plan.key}
              planSaving={planSaving}
              onClick={handlePlanClick}
            />
          ))}
        </div>

        <div style={styles.footerPanel}>
          <div style={styles.footerTitle}>Need a custom FinOps rollout?</div>
          <div style={styles.footerNote}>
            Enterprise plans can include onboarding, custom dashboards, SLA support, SSO, audit logs, and monthly FinOps advisory.
          </div>
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
    background:
      "radial-gradient(circle at top left, rgba(37,99,235,0.18), transparent 32%), radial-gradient(circle at top right, rgba(14,165,233,0.12), transparent 34%), linear-gradient(180deg, #020617 0%, #06101f 100%)",
    color: "white",
    fontFamily: "Inter, Arial, sans-serif",
  },
  backgroundGrid: {
    position: "absolute",
    inset: 0,
    opacity: 0.16,
    backgroundImage:
      "linear-gradient(rgba(148,163,184,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.18) 1px, transparent 1px)",
    backgroundSize: "44px 44px",
    pointerEvents: "none",
  },
  orbOne: {
    position: "absolute",
    top: "100px",
    left: "8%",
    width: "280px",
    height: "280px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(34,211,238,0.14), transparent 68%)",
    filter: "blur(2px)",
    pointerEvents: "none",
  },
  orbTwo: {
    position: "absolute",
    right: "9%",
    bottom: "12%",
    width: "360px",
    height: "360px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(37,99,235,0.16), transparent 70%)",
    filter: "blur(4px)",
    pointerEvents: "none",
  },
  navbar: {
    position: "relative",
    zIndex: 2,
    maxWidth: "1280px",
    margin: "0 auto",
    padding: "24px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    borderBottom: "1px solid rgba(148,163,184,0.16)",
  },
  brandWrap: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },
  brand: {
    fontSize: "19px",
    fontWeight: 950,
    lineHeight: 1,
    letterSpacing: "-0.35px",
  },
  byline: {
    marginTop: "6px",
    color: "#FFFFFF",
    fontSize: "12px",
    fontWeight: 850,
    letterSpacing: "0.18px",
  },
  navButton: {
    background: "linear-gradient(135deg, rgba(37,99,235,0.34), rgba(14,165,233,0.14))",
    border: "1px solid rgba(125,211,252,0.36)",
    color: "#DBEAFE",
    borderRadius: "999px",
    padding: "12px 18px",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 16px 38px rgba(37,99,235,0.20)",
  },
  main: {
    position: "relative",
    zIndex: 2,
    maxWidth: "1280px",
    margin: "0 auto",
    padding: "64px 28px 78px",
    textAlign: "center",
  },
  heroPanel: {
    maxWidth: "900px",
    margin: "0 auto 52px",
  },
  badge: {
    display: "inline-flex",
    padding: "9px 16px",
    borderRadius: "999px",
    background: "rgba(37,99,235,0.20)",
    border: "1px solid rgba(96,165,250,0.42)",
    color: "#DBEAFE",
    fontSize: "13px",
    fontWeight: 950,
    marginBottom: "20px",
    boxShadow: "0 12px 32px rgba(37,99,235,0.18)",
  },
  title: {
    margin: 0,
    fontSize: "clamp(44px, 6vw, 72px)",
    lineHeight: 0.92,
    letterSpacing: "-2.6px",
    fontWeight: 950,
    color: "#FFFFFF",
    textShadow: "0 24px 70px rgba(0,0,0,0.42)",
  },
  subtitle: {
    color: "#B7C9E8",
    fontSize: "19px",
    lineHeight: 1.65,
    maxWidth: "820px",
    margin: "20px auto 24px",
  },
  trustRow: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  trustItem: {
    color: "#A7F3D0",
    background: "rgba(16,185,129,0.10)",
    border: "1px solid rgba(16,185,129,0.22)",
    borderRadius: "999px",
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: 850,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "28px",
    alignItems: "stretch",
  },
  card: {
    position: "relative",
    overflow: "hidden",
    textAlign: "left",
    background: "linear-gradient(180deg, rgba(15,23,42,0.94), rgba(8,13,24,0.98))",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: "28px",
    padding: "36px",
    boxShadow: "0 28px 90px rgba(0,0,0,0.34)",
    backdropFilter: "blur(18px)",
  },
  cardGlow: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "180px",
    height: "180px",
    background: "radial-gradient(circle, rgba(56,189,248,0.12), transparent 68%)",
    pointerEvents: "none",
  },
  highlightCard: {
    border: "2px solid rgba(37,99,235,0.92)",
    boxShadow: "0 32px 110px rgba(37,99,235,0.28)",
    transform: "translateY(-12px)",
  },
  popular: {
    position: "absolute",
    top: "-16px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "linear-gradient(135deg, #38BDF8, #2563EB)",
    color: "white",
    padding: "9px 20px",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: 950,
    whiteSpace: "nowrap",
    boxShadow: "0 18px 38px rgba(37,99,235,0.36)",
  },
  planTag: {
    display: "inline-flex",
    marginBottom: "16px",
    padding: "7px 12px",
    borderRadius: "999px",
    background: "rgba(14,165,233,0.11)",
    border: "1px solid rgba(14,165,233,0.24)",
    color: "#BAE6FD",
    fontSize: "12px",
    fontWeight: 950,
  },
  currentPlan: {
    display: "inline-flex",
    marginBottom: "16px",
    padding: "7px 12px",
    borderRadius: "999px",
    background: "rgba(22,163,74,0.18)",
    border: "1px solid rgba(22,163,74,0.35)",
    color: "#BBF7D0",
    fontSize: "12px",
    fontWeight: 950,
  },
  planName: {
    margin: 0,
    fontSize: "31px",
    fontWeight: 950,
    letterSpacing: "-0.8px",
    color: "#FFFFFF",
  },
  description: {
    color: "#B7C9E8",
    fontSize: "16px",
    lineHeight: 1.65,
    minHeight: "56px",
    margin: "14px 0 26px",
  },
  priceRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "5px",
    marginBottom: "28px",
  },
  price: {
    fontSize: "46px",
    fontWeight: 950,
    letterSpacing: "-1.8px",
    color: "#FFFFFF",
  },
  period: {
    color: "#93C5FD",
    fontSize: "15px",
    fontWeight: 900,
    marginBottom: "8px",
  },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 30px",
    display: "grid",
    gap: "14px",
  },
  featureItem: {
    color: "#E2E8F0",
    fontSize: "16px",
    lineHeight: 1.45,
    display: "flex",
    gap: "11px",
    alignItems: "flex-start",
  },
  check: {
    color: "#67E8F9",
    fontWeight: 950,
    textShadow: "0 0 14px rgba(56,189,248,0.5)",
  },
  primaryButton: {
    width: "100%",
    border: "none",
    borderRadius: "16px",
    padding: "16px 20px",
    background: "linear-gradient(135deg, #38BDF8, #2563EB 58%, #1D4ED8)",
    color: "white",
    fontSize: "16px",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 22px 46px rgba(37,99,235,0.34)",
  },
  secondaryButton: {
    width: "100%",
    border: "1px solid rgba(125,211,252,0.25)",
    borderRadius: "16px",
    padding: "16px 20px",
    background: "rgba(37,99,235,0.82)",
    color: "white",
    fontSize: "16px",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 18px 36px rgba(37,99,235,0.18)",
  },
  currentButton: {
    width: "100%",
    border: "1px solid rgba(22,163,74,0.35)",
    borderRadius: "16px",
    padding: "16px 20px",
    background: "rgba(22,163,74,0.18)",
    color: "#BBF7D0",
    fontSize: "16px",
    fontWeight: 950,
    cursor: "not-allowed",
  },
  footerPanel: {
    marginTop: "38px",
    padding: "24px",
    borderRadius: "24px",
    background: "rgba(15,23,42,0.62)",
    border: "1px solid rgba(148,163,184,0.16)",
  },
  footerTitle: {
    fontSize: "18px",
    fontWeight: 950,
    color: "#FFFFFF",
    marginBottom: "8px",
  },
  footerNote: {
    color: "#94A3B8",
    fontSize: "14px",
    lineHeight: 1.6,
  },
};
